import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Row} from 'react-bootstrap';
import {Flex} from 'shared/components/StyledComponents';
import Icon from 'shared/Icon';
import styled, {css} from 'styled-components';

interface CollapseContainerProps {
    children: React.ReactNode;
    iconName: string;
    headerText: string;
    infoText: string;
    initExpanded?: boolean;
    className?: string;
    asRow?: boolean;
    collapsibleInfoText?: boolean;
}

const CollapseContainer = ({
    children,
    iconName,
    headerText,
    infoText,
    initExpanded = false,
    className,
    asRow = false,
    collapsibleInfoText = false,
}: CollapseContainerProps) => {
    const [expanded, setExpanded] = useState(initExpanded);
    const [scrollIntoView, setScrollIntoView] = useState(false);
    const expandedContentRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const toggleExpanded = useCallback(() => {
        setExpanded((prevState) => !prevState);
        setScrollIntoView(true);
    }, []);

    useEffect(() => {
        if (scrollIntoView && containerRef.current) {
            containerRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
            });
            // reset state once scrolled
            setScrollIntoView(false);
        }
    }, [scrollIntoView]);

    useEffect(() => {
        // initialize expand once
        if (initExpanded) {
            setExpanded(initExpanded);
        }
    }, [initExpanded]);

    const ContainerComponent: React.ElementType = asRow
        ? RowContainer
        : Container;

    return (
        <ContainerComponent
            className={className}
            ref={containerRef}
            style={{scrollMarginBottom: 200}}>
            <Flex
                $padding="0 10px"
                style={{cursor: 'pointer'}}
                $gap="10px"
                $justifyContent="space-between"
                onClick={toggleExpanded}>
                <Flex $gap="12px">
                    <Icon
                        style={{height: 50, width: 50, padding: 3}}
                        iconName={iconName}
                    />

                    <Flex $flexDirection="column" $justifyContent="center">
                        <strong>{headerText}</strong>
                        {(collapsibleInfoText && expanded) ||
                        !collapsibleInfoText ? (
                            <span
                                style={{
                                    fontSize: '0.85rem',
                                }}>
                                {infoText}
                            </span>
                        ) : null}
                    </Flex>
                </Flex>
                <Chevron
                    key={`icon-${expanded ? 'up' : 'down'}`}
                    iconName={expanded ? 'Chevron-up.svg' : 'Chevron-down.svg'}
                />
            </Flex>
            {expanded ? <div ref={expandedContentRef}>{children}</div> : null}
        </ContainerComponent>
    );
};

const Chevron = styled(Icon)`
    width: 20px !important;
`;

const Style = css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    border-radius: 8px;
    border: 1px solid ${({theme}) => theme.colors.primary?.main};
`;

const RowContainer = styled(Row)`
    ${Style}
`;

const Container = styled.div`
    ${Style}
`;

export default CollapseContainer;
